import React, { useEffect, useMemo, useRef } from 'react'
import { useAuditFeed } from './use-audit-feed'
import { AuditEntry } from './audit-entry'
import { AuditStory } from './audit-story'
import { groupStories } from './entry-story'
import { dayKey, dayLabel } from './entry-format'
import { ErrorReference } from '../error/error-reference'
import './audit.scss'

// The refusals the timeline names itself, by status (useAuditFeed's
// errorStatus); anything else is the feed's own sentence (coded server text,
// "Busy, try again", or the plain fallback).
const REFUSED = {
  403: "You can't see this history.",
  404: "This record's history isn't available."
}

// The timeline (spec §5): day groups in the viewer's local time, newest
// first, keyset paging. "Load older" is always there; where
// IntersectionObserver exists, reaching it loads the next page by itself
// (useAuditFeed reads one page at a time, so a double trigger is harmless).
//
// caps come from the page (useAuditCapabilities); onPivot(patch) receives a
// filters patch: { actors }, { ip }, { apps }, { request } or { session }.
// historyOf ({ type, id }, the History drawer): child records' entries name
// the child. onItems(items, record) hears every change to the loaded entries
// (record: the record a record= page names, else null; within: the node
// the within filter names, else null).
//
// Stories (owner 2026-10-09): content keys entries that tell one story (a
// build's publish across its init and complete requests; one request about
// one course; entry.story), and with stories=1 never ends a page inside
// one. The timeline shows each story once, where its newest entry is
// (groupStories): a story of one is an AuditEntry, more is an AuditStory.
// A story whose entries arrive on two pages (one longer than a page's
// slack) is joined when the older page comes.
export const AuditTimeline = ({ params, enabled = true, context, onPivot, emptyText = 'No activity in this time range.', sys = false, caps = {}, scrollRoot, historyOf, onItems }) => {
  const feedParams = useMemo(() => (params ? { ...params, stories: '1' } : params), [params])
  const feed = useAuditFeed(feedParams, { enabled })
  const sentinel = useRef(null)
  const { loadMore, hasMore } = feed
  const onItemsRef = useRef(onItems)
  onItemsRef.current = onItems

  useEffect(() => {
    if (onItemsRef.current) onItemsRef.current(feed.items, feed.record, feed.within)
  }, [feed.items, feed.record, feed.within])

  useEffect(() => {
    if (!hasMore || !sentinel.current || typeof window.IntersectionObserver === 'undefined') return undefined
    const io = new window.IntersectionObserver(es => es.some(x => x.isIntersecting) && loadMore(), { root: scrollRoot || null, rootMargin: '120px' })
    io.observe(sentinel.current)
    return () => io.disconnect()
  }, [hasMore, feed.items.length, loadMore, scrollRoot])

  const groups = useMemo(() => {
    const out = []
    groupStories(feed.items).forEach(story => {
      const first = story.items[0]
      const k = dayKey(first.occurredAt)
      if (!out.length || out[out.length - 1].key !== k) out.push({ key: k, label: dayLabel(first.occurredAt), stories: [] })
      out[out.length - 1].stories.push(story)
    })
    return out
  }, [feed.items])

  if (!feed.items.length && (feed.error || REFUSED[feed.errorStatus])) {
    return (
      <>
        <p className="sw-hint au-more-row" role="alert">{REFUSED[feed.errorStatus] || feed.error}</p>
        <ErrorReference error={feed.failure} />
      </>
    )
  }
  if (!feed.items.length) {
    return feed.loading || !enabled ? <p className="sw-hint au-more-row" aria-busy={feed.loading}>{feed.loading ? 'Loading…' : ''}</p> : <p className="sw-hint au-more-row">{emptyText}</p>
  }

  return (
    <div className="au-timeline" aria-busy={feed.loading}>
      {groups.map(g =>
        <section key={g.key} aria-labelledby={`au-day-${g.key}`}>
          <h3 className="au-day" id={`au-day-${g.key}`}>{g.label}</h3>
          <ol className="au-tl">
            {g.stories.map(st => st.items.length > 1
              ? <AuditStory key={st.key} entries={st.items} sys={sys} caps={caps} onPivot={onPivot} />
              : <AuditEntry key={st.items[0].id} entry={st.items[0]} sys={sys} caps={caps} onPivot={onPivot} context={context} historyOf={historyOf} />)}
          </ol>
        </section>)}
      {feed.error && <p className="text-danger small au-more-row" role="alert">{feed.error}</p>}
      {feed.error && <ErrorReference error={feed.failure} />}
      <div className="au-more-row" ref={sentinel}>
        {feed.loading ? 'Loading…' : hasMore
          ? <button type="button" className="btn sw-btn btn-sm" onClick={loadMore}>Load older</button>
          : context === 'history' ? 'Start of history.' : 'No older entries in this time range.'}
      </div>
    </div>
  )
}
